import { FlightResponse, GetFlightGroupResponse } from '@orbit/api-types'

export const getFlightCode = (leg: FlightResponse): string => {
	const parts: string[] = []
	if (leg.airlineIata) parts.push(leg.airlineIata)
	if (leg.flightNumber) parts.push(leg.flightNumber)
	return parts.join(' ') || 'Flight'
}

export const getRouteString = (leg: FlightResponse): string => {
	const dep = leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? '—'
	const arr = leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? '—'
	return `${dep} → ${arr}`
}

export const formatUnixDateTime = (unix: number | null | undefined): string | null => {
	if (unix == null) return null
	const date = new Date(unix * 1000)
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
}

export const formatUnixDateOnly = (unix: number | null | undefined): string | null => {
	if (unix == null) return null
	const date = new Date(unix * 1000)
	return date.toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

export const formatUnixTimeOnly = (unix: number | null | undefined): string | null => {
	if (unix == null) return null
	const date = new Date(unix * 1000)
	return date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
}

/** Parses "YYYY-MM-DD HH:MM:SS" (wall-clock) into a display string. */
export const formatWallClockStr = (str: string | null | undefined): string | null => {
	if (!str) return null
	const match = str.match(/^(\d{4})-(\d{2})-(\d{2})( (\d{2}):(\d{2}):(\d{2}))?$/)
	if (!match) return str
	const [, y, mo, d, , h, mi] = match
	const date = new Date(Number(y), Number(mo) - 1, Number(d), h ? Number(h) : 0, mi ? Number(mi) : 0)
	if (h) {
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		})
	}
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export const getDepartureDisplay = (leg: FlightResponse): string | null => {
	if (leg.departureTime != null) {
		return formatUnixDateTime(leg.departureTime)
	}
	return formatWallClockStr(leg.departureTimeStr)
}

export const getArrivalDisplay = (leg: FlightResponse): string | null => {
	if (leg.arrivalTime != null) {
		return formatUnixDateTime(leg.arrivalTime)
	}
	return formatWallClockStr(leg.arrivalTimeStr)
}

export const getDurationString = (minutes: number | null | undefined): string | null => {
	if (minutes == null) return null
	const h = Math.floor(minutes / 60)
	const m = minutes % 60
	return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export const getGroupEarliestDeparture = (group: GetFlightGroupResponse): number | null => {
	let earliest: number | null = null
	for (const leg of group.flights) {
		if (leg.departureTime != null) {
			if (earliest == null || leg.departureTime < earliest) {
				earliest = leg.departureTime
			}
		}
	}
	return earliest
}

export const sortGroupsByDeparture = (groups: GetFlightGroupResponse[]): GetFlightGroupResponse[] => {
	return [...groups].sort((a, b) => {
		const aTime = getGroupEarliestDeparture(a) ?? Number.MAX_SAFE_INTEGER
		const bTime = getGroupEarliestDeparture(b) ?? Number.MAX_SAFE_INTEGER
		return aTime - bTime
	})
}

/** Splits "AC123" into ("AC", "123"). */
export const splitFlightCode = (code: string): { airlineIata: string; flightNumber: string } => {
	const trimmed = code.trim().replace(/\s/g, '').toUpperCase()
	const digitIndex = trimmed.search(/\d/)
	if (digitIndex === -1) {
		return { airlineIata: trimmed, flightNumber: '' }
	}
	return {
		airlineIata: trimmed.slice(0, digitIndex),
		flightNumber: trimmed.slice(digitIndex),
	}
}

/** Converts a datetime-local input value "YYYY-MM-DDTHH:MM" into wall-clock "YYYY-MM-DD HH:MM:SS". */
export const datetimeLocalToWallClock = (value: string): string => {
	if (!value) return ''
	const [datePart, timePart = '00:00'] = value.split('T')
	const [h, mi] = timePart.split(':')
	return `${datePart} ${(h || '00').padStart(2, '0')}:${(mi || '00').padStart(2, '0')}:00`
}

/** Converts wall-clock "YYYY-MM-DD HH:MM:SS" into datetime-local "YYYY-MM-DDTHH:MM". */
export const wallClockToDatetimeLocal = (str: string | null | undefined): string => {
	if (!str) return ''
	const match = str.match(/^(\d{4}-\d{2}-\d{2})( (\d{2}):(\d{2}))?/)
	if (!match) return ''
	const [, datePart, , h, mi] = match
	if (!h) return `${datePart}T00:00`
	return `${datePart}T${h}:${mi}`
}
